import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, X } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

/**
 * Inline transcript preview box — shown above the composer after a successful STT.
 *
 * Speed-first: NOT a modal. Inline, editable, with a primary "Wyślij do HussarAi"
 * button and a secondary "Anuluj" link.
 *
 * Props:
 *   transcript       — initial text from STT
 *   onSend(text)     — called with the (possibly edited) text
 *   onCancel()       — dismiss without sending
 */
const VoiceTranscriptPreview = ({ transcript, onSend, onCancel }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(transcript || '');
  const textareaRef = useRef(null);

  useEffect(() => {
    setText(transcript || '');
  }, [transcript]);

  // Autofocus when the preview appears so the user can edit immediately
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.focus();
      // place caret at end
      const len = ta.value.length;
      try {
        ta.setSelectionRange(len, len);
      } catch {
        /* noop */
      }
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSend(text.trim());
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="hussar-transcript-preview" data-testid="voice-transcript-preview">
      <div className="hussar-transcript-preview-header">
        <span className="hussar-transcript-preview-title">
          {t('voice.previewHeader')}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="hussar-transcript-preview-close"
          aria-label={t('voice.cancel')}
          data-testid="voice-transcript-cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <Textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={3}
        className="hussar-transcript-preview-input"
        data-testid="voice-transcript-textarea"
      />
      <div className="hussar-transcript-preview-actions">
        <button
          type="button"
          onClick={onCancel}
          className="hussar-transcript-preview-cancel"
          data-testid="voice-transcript-cancel-link"
        >
          {t('voice.cancel')}
        </button>
        <Button
          onClick={() => onSend(text.trim())}
          disabled={!text.trim()}
          className="hussar-btn-primary"
          data-testid="voice-transcript-send"
        >
          <span>{t('voice.send')}</span>
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default VoiceTranscriptPreview;
